import { Banner, DataTable, PageHeader, SegmentedControl, StatusBadge, sortRows, type Column, type SortState } from "@flexfreight/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { api, ApiError } from "../../api/client";
import { useConsoleMe } from "../../auth/context";
import { carrierName } from "./Shipments";

type Row = {
  carrier: string;
  mode: string;
  enabled: boolean;
  credentialsSaved: boolean;
  lastTest: { at: string; ok: boolean | null; message: string | null } | null;
  quoted: number;
  responseRatePct: number | null;
  shipments: number;
  delivered: number;
  onTimePct: number | null;
  exceptions: number;
  staleTracking: number;
  api: { calls: number; successRate: number | null; p50Ms: number | null; p95Ms: number | null; rateCalls: number; bookCalls: number; topErrors: { code: string; count: number }[] } | null;
};
type Health = { days: number; callsDays: number; totalQuotes: number; carriers: Row[] };
const when = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const pct = (v: number | null) => (v === null ? "-" : `${v}%`);
const SORT_VALUE: Record<string, (r: Row) => string | number | null> = {
  carrier: (r) => carrierName(r.carrier),
  quoted: (r) => r.quoted,
  calls: (r) => r.api?.calls ?? null,
  shipments: (r) => r.shipments,
  ontime: (r) => r.onTimePct,
  exceptions: (r) => r.exceptions,
  stale: (r) => r.staleTracking,
};

/** Freight > Carrier health: connection, quoting and delivery health per carrier, from real rows. Read-only. */
export function CarrierHealthPage() {
  const me = useConsoleMe();
  const [days, setDays] = useState<"7" | "30" | "90">("30");
  const [sort, setSort] = useState<SortState>(null);
  const q = useQuery({ queryKey: ["console", "carrier-health", days], queryFn: () => api<Health>(`/v1/console/carrier-health?days=${days}`), placeholderData: keepPreviousData });
  const h = q.data;
  const rows = sortRows(h?.carriers ?? [], sort, (r, k) => SORT_VALUE[k]?.(r) ?? null);
  const columns: Column<Row>[] = [
    { key: "carrier", header: "Carrier", primary: true, sortable: true, width: "9rem", render: (c) => <span className="ffa-cell-strong">{carrierName(c.carrier)}</span> },
    {
      key: "connection",
      header: "Connection",
      render: (c) => (
        <>
          <StatusBadge domain="carrierConnection" value={c.enabled ? c.mode : "off"} />
          <span className="ffa-muted ffa-block">
            {c.credentialsSaved ? "Keys saved" : "No keys"}
            {c.lastTest ? ` · test ${c.lastTest.ok ? "passed" : "failed"} ${when.format(new Date(c.lastTest.at))}` : " · never tested"}
          </span>
        </>
      ),
    },
    { key: "quoted", header: "Answered quotes", numeric: true, sortable: true, width: "9rem", render: (c) => <>{c.quoted}{c.responseRatePct !== null ? <span className="ffa-muted ffa-block">{pct(c.responseRatePct)} of quotes</span> : null}</> },
    {
      key: "calls",
      header: `API calls (${h?.callsDays ?? days} d)`,
      numeric: true,
      sortable: true,
      width: "10rem",
      render: (c) =>
        c.api ? (
          <>
            {c.api.calls} · {c.api.successRate === null ? "-" : `${Math.round(c.api.successRate * 1000) / 10}% ok`}
            <span className="ffa-muted ffa-block">{c.api.p95Ms !== null ? `p95 ${(c.api.p95Ms / 1000).toFixed(1)} s` : ""}{c.api.topErrors[0] ? ` · ${c.api.topErrors[0].code.replace(/_/g, " ")} ×${c.api.topErrors[0].count}` : ""}</span>
          </>
        ) : (
          <span className="ffa-muted">None</span>
        ),
    },
    { key: "shipments", header: "Shipments", numeric: true, sortable: true, width: "7.5rem", render: (c) => <>{c.shipments}{c.shipments ? <span className="ffa-muted ffa-block">{c.delivered} delivered</span> : null}</> },
    { key: "ontime", header: "On time", numeric: true, sortable: true, width: "6.5rem", render: (c) => pct(c.onTimePct) },
    { key: "exceptions", header: "Exceptions", numeric: true, sortable: true, width: "7rem", render: (c) => (c.exceptions ? <Link to={`/shipments?bucket=exception&carrier=${c.carrier}`}>{c.exceptions}</Link> : 0) },
    { key: "stale", header: "No update 24 h", numeric: true, sortable: true, width: "8rem", render: (c) => (c.staleTracking ? <span className="ffa-cell-strong">{c.staleTracking}</span> : 0) },
  ];
  return (
    <section className="ffa-page">
      <PageHeader
        title="Carrier health"
        subtitle={`How each carrier is connected, how often it answers quote requests, and how its shipments are doing.${h ? ` ${h.totalQuotes} quote${h.totalQuotes === 1 ? "" : "s"} in the last ${h.days} days.` : ""}`}
        secondaryActions={<SegmentedControl label="Period" options={[{ value: "7", label: "7 days" }, { value: "30", label: "30 days" }, { value: "90", label: "90 days" }]} value={days} onChange={setDays} />}
      />
      {h && h.carriers.every((c) => c.mode !== "live") ? (
        <Banner tone="warning" title="No live carriers" actions={me.capabilities.includes("carriers:manage") ? <Link className="ffx-btn ffx-btn--sm" to="/admin/carriers">Carriers &amp; API keys</Link> : null}>
          Every carrier is in mock or sandbox mode, so quote and API numbers come from test systems.
        </Banner>
      ) : null}
      <DataTable
        caption={`Carrier health, last ${days} days`}
        columns={columns}
        rows={rows}
        rowKey={(c) => c.carrier}
        loading={q.isPending || q.isFetching}
        error={q.isError ? { title: "Couldn't load carrier health", message: q.error instanceof ApiError ? q.error.message : undefined, onRetry: () => void q.refetch() } : null}
        empty="No carriers set up yet."
        sort={sort}
        onSortChange={setSort}
        rowTone={(c) => (c.exceptions > 0 ? "danger" : c.staleTracking > 0 ? "warning" : null)}
      />
      <p className="ffa-muted">On time means delivered by the carrier&apos;s estimated date (Eastern time). API call outcomes are kept for 30 days.</p>
    </section>
  );
}
